import { isBoolean } from 'lodash';
import { Moment } from 'moment';
import { AptAddressType } from '../../common/enums/apttus/apt-address-type';
import { AptBillType } from '../../common/enums/apttus/apt-bill-type';
import { AptCommodityType, AptCommodityTypeOfSale } from '../../common/enums/apttus/apt-commodity-typeof-sale';
import { AptDoiConfirmModeType } from '../../common/enums/apttus/apt-doi-confirm-mode-type';
import { AptDoiStatusType } from '../../common/enums/apttus/apt-doi-status-type';
import { HoursBundle } from '../../common/enums/apttus/apt-hours-bundle';
import { AptLineStatus } from '../../common/enums/apttus/apt-line-status';
import { AptContactRole } from '../../common/enums/apttus/apt-mb-representative';
import { AptPaymentFrequency } from '../../common/enums/apttus/apt-payment-frequency';
import { AptPaymentInstrument } from '../../common/enums/apttus/apt-payment-instrument';
import { AptPaymentType } from '../../common/enums/apttus/apt-payment-type';
import { AptPowerVoltage } from '../../common/enums/apttus/apt-power-voltage';
import { AptProductFamily } from '../../common/enums/apttus/apt-product-family';
import { AptProductType } from '../../common/enums/apttus/apt-product-type';
import { AptQuoteStatus } from '../../common/enums/apttus/apt-quote-status';
import { AptQuoteSubStatus } from '../../common/enums/apttus/apt-quote-sub-status';
import { AptSaleabilityOperationType } from '../../common/enums/apttus/apt-sales-process';
import { D365ConfirmationType } from '../../common/enums/d365/d365-confirmation-type';
import { D365SignatureType } from '../../common/enums/d365/d365-signature-type';
import { ClasseDiPrelievo } from '../../common/enums/shared/classe-di-prelievo';
import { DestinationUse } from '../../common/enums/shared/destination-use.enum';
import { GasMeterClass } from '../../common/enums/shared/gas-meter-class';
import { PdrType } from '../../common/enums/shared/pdr-type';
import { isCommodityFamily, isOptionFamily } from '../../common/functions/verifications.functions';
import { PaymentAsset } from '../../common/models/app/payment-tools.response';
import { EglCartPAVLight } from '../../common/models/apttus/tables/pav/egl-cart-p-a-v-light';
import { VirtualAgentBackOffice } from '../../common/models/user/agent';
import { HistorizedAgencyBranch } from '../../common/models/user/historized-agency-branch';
import { CheckBalanceResult } from '../../modules/common/order-entry/models/check-balance.response';
import { FormDatiPagamento } from '../../modules/common/order-entry/models/form-dati-pagamento';
import { FotoDocumenti, SelfCertificationData } from '../../modules/common/order-entry/models/foto-documenti';
import { PartnerShipInfo } from '../../modules/common/partnership/models/partnership-info';
import { Indirizzi, Indirizzo } from '../../modules/switch-in/order-entry/models/indirizzi';
import { OcrData } from '../../modules/switch-in/order-entry/models/ocr-data';
import { QuestionIvassState } from '../../modules/switch-in/products/models/ivass-question';
import { FlowType } from './flow-type';

export class OrderEntryState {
    version: 1;
    flowType: FlowType;
    currentProductV2Id?: number;
    partNumber?: string | SalesProcessPartNumber;
    operationType: AptSaleabilityOperationType;
    assetIntegrationId: string;
    pod: string;
    pdr: string;
    pdf?: string;
    cartInfo: CartInfo;
    infoProdotti?: ProductInfo[];
    indirizzi?: Indirizzi;
    datiPagamento?: FormDatiPagamento;
    fotoDocumenti: FotoDocumenti;
    realEstateOwnership: TitolaritaImmobile;
    firma?: Firma;
    mp3Info?: Mp3Info;
    uploadPdfPlicoInfo?: PlicoPdfInfo;
    numeroPlico: ContractCode;
    numeroDomiciliazione: string;
    tipoEsecuzione: TipoEsecuzione;
    fornitureAttive: FornituraEsistente[];
    selectFornitureAttiveSelezionate: FornituraEsistente[];
    privacyTrattDatiPers: DomandePrivacy;
    quoteStateModel?: QuoteStateModel;
    verificaContatto: VerificaContatto;
    agencyBranchForMonitoring?: string;
    branchAgenziaAgente?: HistorizedAgencyBranch;
    skipPod = false;
    skipPdr = false;
    skipAteco: boolean;
    isWinBack? = false;
    winBackType? = WinBackType.None;
    plicoUrl: string;
    anagraficaMb: DatiAnagraficiMB;
    atecoMb: AtecoMB;
    ocrData: OcrData;
    potenzaImpegnataMb?: PotenzaImpegnataMB;
    fatturazioneElettronica: FatturazioneElettronica;
    // troubleshootingPowerDone = false;
    // troubleshootingGasDone = false;
    impersonatedAgent?: VirtualAgentBackOffice;
    invalidCf?: InvalidCf;
    creditCheckStatus?: CreditCheckStatus;
    deferredSaleEnabled: boolean;
    powerConsumption?: PowerConsumption;
    powerHomeDropdownOption?: CommodityEstateCardinality;
    gasConsumption?: GasConsumption;
    gasHomeDropdownOption?: CommodityEstateCardinality;
    potenzaImpegnata?: string;
    responsabilitaCreditCheckModal: boolean;
    termination?: Termination;
    activation?: Activation;
    costs?: CostData;
    selfCertification: SelfCertificationData;
    ivassAnswers: QuestionIvassState[];
    insuranceDummyProductId: string;
    partnership: PartnerShipInfo;
    quoteId?: string;
    combinedSaleQuoteId?: string;
    combinedSale: boolean;
    commodityCartId?: string;
    administrativeTechnicalData: AdministrativeTechnicalData;
    domiciliationStandAlone: Domiciliation;
    incident?: Incident;
    deliveryChannel: AptBillType; // in casi come la domiciliazione il campo arriva valorizzato da apttus (state di partenza)
    supplyCode?: string;
    /**
     * @deprecated Non utilizzato
     */
    supplyClass?: string;
    destinationUse: DestinationUse;
    sendCommunications: boolean;
    requiredPower: string;
    availablePower: number;
    voltage: AptPowerVoltage;

    constructor() {
        this.flowType = FlowType.SwitchIn;
        this.privacyTrattDatiPers = {};
        this.firma = new Firma();
        this.mp3Info = new Mp3Info();
        this.quoteStateModel = new QuoteStateModel();
        this.cartInfo = new CartInfo();
        this.indirizzi = new Indirizzi();
        this.impersonatedAgent = null;
        this.creditCheckStatus = new CreditCheckStatus();
    }
}

export class ContractCode {
    code: string;
    channelName: string;
    signatureType: D365SignatureType;
}

export class CartInfo {
    cartId: string;
    ownerDagCode: string;
}
export class TipoEsecuzione {
    passaggioRapidoCommodity?: boolean;
    dataStimataCommodity?: Date;
    passaggioRapidoManutenzione?: boolean;
    dataStimataManutenzione?: Date;
    isRataFissaDatesEquals?: boolean;
    dataStandardCommodity?: Date;
    dataAnticipataCommodity?: Date;
    isRataFissaCP?: boolean;
}

export class ProductInfo {
    id: string;
    code: string;
    nome: string;
    productType: AptProductType;
    family?: AptProductFamily;
    configuration: ProductConf;
    sku: string;
    uniqueProductCode: string;
    powerOrGas: AptCommodityType;
    addressType: AptAddressType;
    lineItemId: string;
    status: AptLineStatus;
    optionsConfig: string[];
    discountsPromo: string[];
    privacyRequired: boolean;
    isExpired: boolean;
    /**Indica se si tratta di un prodotto "A Nuovo" oppure di un prodotto "A Vecchio" */
    isAtNew: boolean;
    podPdr: string;
    supplyAddress: string;
    commAddress: string;
    partnership?: string;
    supplyCode: string;
    isAutogenerate?: boolean;

    constructor(
        id: string,
        lineItemId: string,
        type: AptProductType,
        family: AptProductFamily,
        name: string,
        code: string,
        addressType: AptAddressType,
        pav: EglCartPAVLight,
        privacyRequired: boolean,
        commodityType: AptCommodityType
    ) {
        const addressTypeT = !isOptionFamily(family) ? addressType || AptAddressType.Fornitura : null;
        this.id = id;
        this.lineItemId = lineItemId;
        this.productType = type;
        this.nome = name;
        this.code = code;
        this.family = family;
        this.addressType = addressTypeT;
        this.configuration = {
            paymentInstrument: pav.egl_Payment_Instrument__c,
            hoursBundle: pav.egl_Hours_Bundle__c,
            seasonalOption: pav?.egl_seasonal_option__c,
            billType: pav.egl_Invoice_Shipping_Method__c,
            orderNumber: pav.egl_selling_order_number__c,
            paymentFrequency: pav.egl_Payment_Frequency__c,
            installmentDuration: pav.egl_installment_duration__c,
            paymentType: pav.egl_Payment_Type__c,
            isCombinedSaleInsurance: pav.egl_combined_sale_insurance__c !== 'No',
            billCharge: pav.egl_addebitoinbolletta__c?.toUpperCase() === 'SI',
            vulnerabilityOver75: pav.egl_vulnerabilitytype_over75__c,
            vulnerabilitySocialBonus: pav.egl_vulnerabilitytype_socialbonus__c,
            vulnerabilitySaeMapre: pav.egl_vulnerabilitytype_saemapre__c,
            vulnerabilityDisabled: pav.egl_vulnerabilitytype_disabled__c,
            modemSupply: pav.egl_bb_modemsupply__c,
            relatedContractCode: pav.egl_contact_customer__c,
            categoryCustomer: pav.egl_category_customer__c,
            technology: pav.egl_bb_technology__c,
            wholesaler: pav.egl_bb_wholesaler__c,
            speedprofile: pav.egl_bb_speedprofile__c,
            voice: pav.egl_bb_voice__c,
            cluster: pav.egl_cluster__c
        };

        if (isCommodityFamily(<AptProductFamily>family)) {
            this.powerOrGas = commodityType || pav.egl_commodity_supplytype__c;
        } else if (family === AptProductFamily.Assicurazione) {
            this.powerOrGas = pav.egl_Insurance_Type__c;
        }
        this.podPdr =
            (commodityType || pav.egl_commodity_supplytype__c) === AptCommodityType.Gas
                ? pav.egl_PDR__c
                : pav.egl_POD__c;
        if (pav?.egl_Place_Name__c && pav?.egl_Address__c) {
            this.supplyAddress = `${pav.egl_Place_Name__c} ${pav.egl_Address__c} ${pav.egl_Civic_Number__c}, ${pav.egl_City__c}, ${pav.egl_Province__c}, ${pav.egl_Postal_Code__c}`;
        }
        this.optionsConfig = [];
        this.discountsPromo = [];
        this.privacyRequired = privacyRequired;
        if (pav.egl_partnership__c) {
            this.partnership = pav.egl_partnership__c.toLowerCase();
        }
    }
}

export class ProductConf {
    billType: AptBillType;
    hoursBundle: HoursBundle;
    seasonalOption?: string;
    paymentInstrument: AptPaymentInstrument;
    orderNumber: string;
    paymentFrequency: AptPaymentFrequency;
    paymentType: AptPaymentType;
    installmentDuration: number;
    isCombinedSaleInsurance: boolean;
    /**
     * @description egl_addebitoinbolletta__c
     */
    billCharge: boolean;
    vulnerabilityOver75: boolean;
    vulnerabilitySocialBonus: boolean;
    vulnerabilitySaeMapre: boolean;
    vulnerabilityDisabled: boolean;
    modemSupply: boolean;
    relatedContractCode: string;
    categoryCustomer: string;
    technology: string;
    wholesaler: string;
    speedprofile: string;
    voice: string;
    cluster: string;
}

export class VerificaContatto {
    tipoVerifica: 'sms' | 'email' | 'both';
    dataOraInvio: Date;
}

export class PowerConsumption {
    typeOfUse? = '';
    value?: number;
    declared?: number;
    transcodingValue?: number;
    voltage?: string;
    constructor(value?: number, declared?: number, transcodingValue?: number, voltage?: string) {
        this.value = value;
        this.declared = declared;
        this.transcodingValue = transcodingValue;
        this.voltage = voltage;
    }
}

export class GasConsumption {
    typeOfUse = '';
    consumption: number;
    selfReading?: SelfReading;
    constructor(type?: string, consumption?: number) {
        if (type) {
            this.typeOfUse = type;
        }
        if (consumption) {
            this.consumption = +consumption;
        }
    }
}

export class FornituraInAttivazione {
    podPdr: string;
    pdf: string;
    powerOrGas: AptCommodityType;
    quoteId: string;
    toponimo: string;
    via: string;
    civico: string;
    citta: string;
    provincia: string;
    region?: string;
    cap: string;
}
export class FornituraEsistente extends FornituraInAttivazione {
    assetIntegrationId: string;
    indirizzoCompleto: string;
    toponimo: string;
    isWinBack: boolean;
    isResidente: boolean;
    dataPresuntoSwitchOut: string;
    stato: string; // 'ATTIVO' indica se la fornitura è attiva
    atecoCode: string;
    datiInizio: string;
    codiceIstat: string;
    modalitaOperativa?: string;
}

export class PowerAdministrativeTechnicalData {
    potenzaDisponibile: number;
    potenzaImpegnata: number;
    tensione: AptPowerVoltage;

    constructor(data: PowerAdministrativeTechnicalData) {
        this.potenzaDisponibile = data?.potenzaDisponibile || null;
        this.potenzaImpegnata = data?.potenzaImpegnata || null;
        this.tensione = data?.tensione || null;
    }
}

export class GasAdministrativeTechnicalData {
    // distributore: string;
    // pivaDistributore: string;
    tipologiaPdr: PdrType;
    matricolaMisuratore: string;
    cifreMisuratore: number;
    letturaMisuratore: number;
    classeMisuratore: GasMeterClass;
    gruppoMisuratoreIntegrato: boolean;
    coefficienteCorrettivoC?: number;
    cifreCorrettore?: number;
    matricolaCorrettore?: string;
    letturaCorrettore?: number;
    cityGate: string;
    progressivoVolumiAnnuo: number;
    istatComune: string;
    pIvaDistributore: string;
    classeDiPrelievo: ClasseDiPrelievo;

    constructor(data?: GasAdministrativeTechnicalData) {
        // this.distributore = data?.distributore || null;
        // this.pivaDistributore = data?.pivaDistributore || null;
        this.tipologiaPdr = data?.tipologiaPdr || null;
        this.matricolaMisuratore = data?.matricolaMisuratore || null;
        this.cifreMisuratore = data?.cifreMisuratore ?? null;
        this.letturaMisuratore = data?.letturaMisuratore ?? null;
        this.classeMisuratore = data?.classeMisuratore || null;
        this.gruppoMisuratoreIntegrato = isBoolean(data?.gruppoMisuratoreIntegrato)
            ? data?.gruppoMisuratoreIntegrato
            : null;
        this.coefficienteCorrettivoC = data?.coefficienteCorrettivoC ?? null;
        this.cifreCorrettore = data?.cifreCorrettore ?? null;
        this.matricolaCorrettore = data?.matricolaCorrettore || null;
        this.letturaCorrettore = data?.letturaCorrettore ?? null;
        this.cityGate = data?.cityGate || null;
        this.progressivoVolumiAnnuo = data?.progressivoVolumiAnnuo || null;
        this.istatComune = data?.istatComune || null;
        this.pIvaDistributore = data?.pIvaDistributore || null;
        this.classeDiPrelievo = data?.classeDiPrelievo || null;
    }
}

export class AdministrativeTechnicalData {
    power?: PowerAdministrativeTechnicalData;
    gas?: GasAdministrativeTechnicalData;
    constructor(
        type: AptCommodityTypeOfSale,
        powerData?: PowerAdministrativeTechnicalData,
        gasData?: GasAdministrativeTechnicalData
    ) {
        if (type === AptCommodityTypeOfSale.Gas) {
            this.gas = new GasAdministrativeTechnicalData(gasData);
        } else if (type === AptCommodityTypeOfSale.Power) {
            this.power = new PowerAdministrativeTechnicalData(powerData);
        } else {
            this.gas = new GasAdministrativeTechnicalData(gasData);
            this.power = new PowerAdministrativeTechnicalData(powerData);
        }
    }
}

export interface DomandePrivacy {
    [Key: string]: boolean;
    wantsEditConsents?: boolean;
}

export class Firma {
    d365SignatureType?: D365SignatureType; // Modalità firma
    confirmationType?: D365ConfirmationType; // Modalità conferma
    canBeModified?: boolean;
    signedDate?: Date | string = null; // Data firma
    doiStatus?: AptDoiStatusType; // DOI status - non settare
    confirmMode?: AptDoiConfirmModeType; // Modalità conferma
    checkCallBlocking?: boolean; // Check call bloccante
}

export class Mp3Info {
    uploaded: boolean;
    uploadDate: Date;
    type: 'DOI' | 'VO';
    size: number;
    fileName: string;
}

export class PlicoPdfInfo {
    uploaded: boolean;
    uploadDate: Date;
    size: number;
    fileName: string;
    plicoCode: string;
}

export class QuoteStateModel {
    status?: AptQuoteStatus | string;
    subStatus?: AptQuoteSubStatus | string;
    constructor(status?: AptQuoteStatus, subStatus?: AptQuoteSubStatus) {
        if (status && subStatus) {
            this.status = status;
            this.subStatus = subStatus;
        } else {
            this.status = AptQuoteStatus.InCompilazione;
        }
    }
}

export class DatiAnagraficiMB {
    isAtoka: boolean;
    piva: string;
    cf: string;
    companyName: string;
    legalForm: string;
    companyAddress: Indirizzo;
    nameLegal: string;
    surnameLegal: string;
    cfLegal: string;
    representativeType: AptContactRole = AptContactRole.RappresentanteLegale;
}

export class AtecoMB {
    codeAtecoPower: string;
    codeAtecoGas: string;
    validated: boolean;
    attivita?: string;
    settore?: string;
    denominazione?: string;
}

export class PotenzaImpegnataMB {
    consumoInpt: number;
    potenzaImpegnataCmb: string;
    potenzaDisponibileInp: string;
    livelloTensione: string;
    potenzaImpInputManuale: string;
}

export class FatturazioneElettronica {
    codiceDestinatario?: string;
    pec: string;
}

export class InvalidCf {
    siebelOldCf: string;
    siebelCustomerCode: string;
}

export class CreditCheckStatus {
    canProceed?: boolean; //TODO Lorenzo - Quando verrà dismesso il vecchio Credit Check, fai diventare obbligatorio
    errorCode?: string;
    /**Indica il codice fiscale o la P.Iva al quale è associato il risultato del credit check */
    code?: string;
    /**Indica la data nella quale è stato fatto il credit check */
    checkDate?: Moment;
    ccDetails: CreditCheckDetails;
    taxVatDetails?: TaxVatDetails;
    callScorecard: boolean;
    isFakeResponse?: boolean; // nel caso in cui la response è moccata(es credit check - scenari amministrativi) questa variabile è true
}
export interface CreditCheckDetails {
    blacklist: string;
    blacklist2: string;
    newBlacklist: string;
    whitelist: string;
    /**Esito complessivo di Insoluto NDS (egl_unsolved__c) */
    unsolvedNds?: string;
    /**Esito di Insoluto NDS relativo al solo IBAN (egl_unsolved_iban__c) */
    unsolvedIbanNds?: string;
    avgInvoiceAmount: string;
    paymentScore?: string;
    /**Cribis = Scorecard = Cerved */
    cribis?: string;
    ceaseReasonCode?: string;
    residualNds: string;
    /**Soglia numerica (stringhizzata) relativa all'insoluto NDS del CF (insoluto_zuora_cf) - Usata da Apttus direttamente dallo state per logiche di RETRY */
    unsolvedTaxCodeNds: string;
    /**Esito di Insoluto NDS relativo al solo CF/P.IVA (egl_unsolved_fc_vat__c) */
    unsolvedTaxCodeNdsResult: string;
    checkBalanceExceeded?: CheckBalanceResult;
}
export class TaxVatDetails {
    sdi?: string;
    scipafi?: 'OK' | 'NON DISPONIBILE' | string;
}

export enum TitolaritaImmobile {
    Proprietario = 'Proprietario',
    Affittuario = 'Affittuario',
    Altro = 'Altro'
}
export class Termination {
    cartId: string;
    isInvoiceDeliveryAddressUpdated: boolean;
    isMortisCausa: boolean;
    termDate: string;
    termContacts: TermContacts[];
    termAppointment: Appointment;
    termCosts: TermCosts[];
}

export class Appointment {
    presence: boolean;
    altContactFName?: string;
    altContactLName?: string;
    altContactPrefix?: string;
    altContactPhone?: string;
    timeSlot?: string;
    interphoneAvailable?: boolean;
    interphoneNotes?: string;
    meters?: Meter[];
    csv: AppointmentCsv[];
}

export class TermContacts {
    oldContact: string;
    confirmContact: boolean;
}

export class TermCosts {
    idAsset: string;
    priceSDR: string;
    priceOPS: string;
}

export class Meter {
    lineItemId: string;
    isAvailable?: boolean;
    location?: string;
    notes?: string;
    name?: string;
    category?: string;
}

export class AppointmentCsv {
    lineItemId: string;
    content: string;
}

export class Domiciliation {
    id: string;
    oldId: string;
    deactivateOldPaymentTool: boolean;
    billingPreferenceId?: string;
    assets?: PaymentAsset[]; //prevalorizzato  dalla createState di apttus. Contiene gli asset interessati dalla DOMICILIAZIONE
    caseID?: string; //gestisce le domiciliazioni aperte dal CASE domiciliation livello 3 di D365
}

export class Activation {
    podActivationInfo?: PodActivationInfo;
    pdrActivationInfo?: PdrActivationInfo;
    propertyTypeSelected?: PropertyTypes;
    actDate?: Date;
    immediateEffect?: boolean;
    actiAppointment?: Appointment;
}

export class PodActivationInfo {
    tensione?: string;
    ultimaPotDisp?: string;
    nuovaPotSel?: string;
    fornitura?: PodInfoLocation;
    identificativiRichiesta?: {
        pivaDistributore?: string;
    };
    ammissibilita?: string;
}

export class PodInfoLocation {
    ubicazioneFornituraPod?: string;
    toponimo?: string;
    via?: string;
    civ?: string;
    cap?: string;
    localita?: string;
    prov?: string;
    scala?: string;
    piano?: string;
    interno?: string;
    istat?: string;
}

export class PdrActivationInfo {
    meterNumber: string;
    potentiality: string;
    meterClass: string;
    vendorCode: string;
    fornitura: PdrInfoLocation;
    statoContatore?: string;
    potentialityFromCheckPdr?: boolean;
}

export class PdrInfoLocation {
    AccessLettPdrEsito?: string;
    AccessPdrEsito?: string;
    CapPdrEsito: string; // cap
    CivicoPdrEsito: string; // number
    ClienteFinalePdrEsito?: string;
    ComunePdrEsito: string; //city
    DataAggDistrPdrEsito?: string;
    DataCostrPdrEsito?: string;
    ErogabPdrEsito?: string;
    ImpDistrPdrEsito?: string;
    IndirPdrEsito: string; // street
    IntPdrEsito?: string;
    IstatComunePdrEsito: string;
    PDRDistrEsito?: string;
    PianoPdrEsito?: string;
    PotenzialitaPdrEsito?: string;
    PressContPdrEsito?: string;
    PrPdrEsito: string; // provincia
    SiglaTopPdrEsito: string; // Toponimo
    TipoAllPdrEsito?: string;
    TipoServPdrEsito?: string;
    UbicPdrEsito?: string;
    ScalaPdrEsito?: string;
}

export class CostRecord {
    id: string;
    name?: string;
    eniCosts: number;
    otherCosts: number;
    total: number;
}
export class CostData {
    records: CostRecord[];
    total?: number;
    skipCosts?: boolean;
}

export class Incident {
    ticketNumber: string;
}

export class SelfReading {
    meterCounter: number;
    meterCounterValidity: boolean;
    meterAdjustmentCounter?: number;
    coeffC?: number;
}

export enum WinBackType {
    None = 'None',
    Gas = 'Gas',
    Power = 'Power',
    Both = 'Both'
}

export enum SalesProcessPartNumber {
    GAS_ACTIVATION_WITH_METER = 'S-PR-003',
    GAS_ACTIVATION_WITHOUT_METER = 'S-PR-001',
    POWER_ACTIVATION_SIMPLE = 'S-PW-001',
    POWER_METRIC_CHECK = 'S-PW-018',
    POWER_INSTANT_VOLTAGE_CHECK = 'S-PW-016',
    POWER_RECORDER_VOLTAGE_CHECK = 'S-PW-017',
    POWER_DECREASE = 'S-PW-019',
    POWER_INCREASE = 'S-PW-014',
    POWER_METER_SHIFT_LESS_10_METERS = 'S-PW-009',
    POWER_METER_SHIFT_MORE_10_METERS = 'S-PW-010',
    POWER_PHASE_CHANGE = 'S-PW-011',
    POWER_TECH_INTERVENTION = 'S-PW-013',
    POWER_VOLTAGE_CHANGE = 'S-PW-015',
    GAS_METER_READING = 'S-PR-012',
    GAS_CHANGE_ACTIVE_METER = 'S-PR-007',
    GAS_ACTIVATION_WITH_METER_DEL_40_14 = 'S-PR-019',
    GAS_ACTIVATION_WITH_ASSESSMENT = 'S-PR-001',
    GAS_QUOTE_AND_ACTIVATION = 'S-PV-001',
    POWER_ACTIVATION_WITH_INSTANCE = 'S-PW-005',
    POWER_CONNECTION_WITH_INSTALLATION_AND_ACTIVATION = 'S-PW-004',
    POWER_PREPOSATO_PA2 = 'S-PW-026'
}

export enum CommodityEstateCardinality {
    First = '1',
    Second = '2'
}

export enum PropertyTypes {
    Singolo = 'Appartamento singolo',
    Stabile = 'Intero stabile'
}
